import React, { useState, useEffect } from 'react';
import { X, Loader, Save } from 'lucide-react';
import { Domain, DomainTransferStatus } from '../../types';
import { domainsService } from './domainsService';
import { useAuth } from '../../hooks/useAuth';

interface Props {
  isOpen: boolean;
  domain: Domain | null;
  onClose: () => void;
  onSuccess: () => void;
}

const TRANSFER_STATUSES: { value: DomainTransferStatus; label: string }[] = [
  { value: 'keep', label: 'Keep (we own/manage)' },
  { value: 'transfer-pending', label: 'Transfer – pending' },
  { value: 'transfer-in-progress', label: 'Transfer – in progress' },
  { value: 'transferred', label: 'Transferred' },
];

export const EditDomainModal: React.FC<Props> = ({ isOpen, domain, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [form, setForm] = useState<Domain | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(domain ? { ...domain } : null);
    setError('');
  }, [domain]);

  if (!isOpen || !form) return null;

  const set = <K extends keyof Domain>(key: K, value: Domain[K]) => {
    setForm(prev => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = async () => {
    if (!form || !user?.email) return;
    setSaving(true);
    setError('');
    try {
      const { id, ...rest } = form;
      await domainsService.updateDomain(id, rest, user.email);
      onSuccess();
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const field = (label: string, key: keyof Domain, type: 'text' | 'textarea' = 'text') => (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      {type === 'textarea' ? (
        <textarea
          value={(form[key] as string) || ''}
          onChange={(e) => set(key, e.target.value as any)}
          rows={3}
          className="w-full px-3 py-2 border rounded-lg text-sm"
        />
      ) : (
        <input
          type="text"
          value={(form[key] as string) || ''}
          onChange={(e) => set(key, e.target.value as any)}
          className="w-full px-3 py-2 border rounded-lg text-sm"
        />
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full my-8 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <div>
            <h2 className="text-xl font-bold">{form.domain}</h2>
            <p className="text-xs text-muted-foreground">Domeneshop ID: {form.domainId}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Internal tracking */}
          <section>
            <h3 className="text-sm font-semibold mb-3 text-primary">Internal tracking</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Transfer status</label>
                <select
                  value={form.transferStatus}
                  onChange={(e) => set('transferStatus', e.target.value as DomainTransferStatus)}
                  className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                >
                  {TRANSFER_STATUSES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              {field('Avdeling', 'avdeling')}
            </div>
            <div className="mt-3">
              {field('Internal notes', 'internalNotes', 'textarea')}
            </div>
          </section>

          {/* Innehaver */}
          <section>
            <h3 className="text-sm font-semibold mb-3 text-primary">Innehaver (eier)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {field('Firmanavn', 'innehaverFirmanavn')}
              {field('Organisasjonsnummer', 'innehaverOrgnr')}
              {field('Fornavn', 'innehaverFornavn')}
              {field('Etternavn', 'innehaverEtternavn')}
              {field('Epost', 'innehaverEpost')}
              {field('Telefon', 'innehaverTelefon')}
              {field('Postadresse', 'innehaverPostadresse')}
              <div className="grid grid-cols-2 gap-3">
                {field('Postnr', 'innehaverPostnummer')}
                {field('Sted', 'innehaverSted')}
              </div>
              {field('Land', 'innehaverLand')}
            </div>
          </section>

          {/* Betalingskontakt */}
          <section>
            <h3 className="text-sm font-semibold mb-3 text-primary">Betalingskontakt</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {field('Firmanavn', 'betalingFirmanavn')}
              {field('Organisasjonsnummer', 'betalingOrgnr')}
              {field('Fornavn', 'betalingFornavn')}
              {field('Etternavn', 'betalingEtternavn')}
              {field('Epost', 'betalingEpost')}
              {field('Telefon', 'betalingTelefon')}
              {field('Postadresse', 'betalingPostadresse')}
              <div className="grid grid-cols-2 gap-3">
                {field('Postnr', 'betalingPostnummer')}
                {field('Sted', 'betalingSted')}
              </div>
              {field('Land', 'betalingLand')}
              {field('Modus', 'betalingModus')}
              {field('Type', 'betalingType')}
              {field('Faktura type', 'fakturaType')}
              {field('Faktura valuta', 'fakturaValuta')}
              {field('Betalingsreferanse', 'betalingsreferanse')}
            </div>
          </section>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{error}</div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader className="animate-spin" size={16} /> : <Save size={16} />}
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
};
